import { useCallback, useEffect, useRef } from 'react';
import * as api from '../utils/api';

// Cross-repo contract (FIX-94) — these three constants MUST match the api
// side exactly. See `contract.md` / CONTEXT.md. Vocabulary is `overlapping`:
// never « doublon », which means a free re-booking in this codebase.
export const OVERLAPPING_PATH = (clientId) =>
  `/clients/${clientId}/courses/overlapping`;
export const OVERLAPPING_DATE_PARAM = 'date';

// Upper bound on how long the click is allowed to wait for the answer. The
// request is fired at mount, so in practice it has long resolved; this only
// caps the pathological case (a socket that never settles) — a warning must
// never be able to hold a booking hostage.
export const DEFAULT_OVERLAPPING_TIMEOUT_MS = 3000;

/**
 * Narrows an api response down to the overlapping course, or null.
 *
 * Fail-open by construction: anything that is not an explicit, well-formed
 * `has_overlap: true` payload reads as "no overlap". `api.get` resolves with
 * the parsed body whatever the HTTP status (a 422 or a 500 body lands here,
 * not in a catch), and resolves with null/undefined on an unparseable or
 * swallowed response — so every one of those degrades silently here.
 *
 * @param {*} response raw body returned by api.get
 * @returns {{ id: number, date: string }|null}
 */
export function readOverlappingCourse(response) {
  if (!response || response.success !== true) return null;

  const { data } = response;
  if (!data || data.has_overlap !== true) return null;

  const { course } = data;
  if (!course) return null;

  // The id is interpolated into a URL opened in a new tab, so it is validated
  // rather than trusted: the contract says `int`, and anything else is a
  // response we do not understand — which means "no overlap", not "warn with
  // whatever came back".
  const id = Number(course.id);
  if (!Number.isInteger(id) || id <= 0) return null;

  return { id, date: course.date };
}

/**
 * Asks the api whether the client already has an active course on or after the
 * date about to be booked. The api owns the rule; the front only reads the
 * answer.
 *
 * The request is fired at mount (the date is frozen by then) and memoised, so
 * the critical action of the funnel — the validate click — adds no latency and
 * a single request covers the whole passage.
 *
 * @param {{ clientId: ?number, date: ?string, timeoutMs: ?number }} params
 * @returns {{ getOverlappingCourse: () => Promise<?{id: number, date: string}> }}
 */
export default function useOverlappingCourse({
  clientId,
  date,
  timeoutMs = DEFAULT_OVERLAPPING_TIMEOUT_MS,
}) {
  const requestRef = useRef(null);

  useEffect(() => {
    if (!clientId || !date) {
      requestRef.current = null;
      return;
    }

    requestRef.current = api
      .get(OVERLAPPING_PATH(clientId), { [OVERLAPPING_DATE_PARAM]: date })
      .then(readOverlappingCourse)
      // Transport failure (api.get rejects): the other half of the fail-open.
      // Attached here so the memoised promise can never reject, and can never
      // surface as an unhandled rejection either.
      .catch(() => null);
  }, [clientId, date]);

  const getOverlappingCourse = useCallback(() => {
    const request = requestRef.current;
    if (!request) return Promise.resolve(null);

    return new Promise((resolve) => {
      const timer = setTimeout(() => resolve(null), timeoutMs);
      request.then((overlappingCourse) => {
        clearTimeout(timer);
        resolve(overlappingCourse);
      });
    });
  }, [timeoutMs]);

  return { getOverlappingCourse };
}
